import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BankAccountService from "services/lms/BankAccountService";
import { notification } from "antd";

export const initialState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
  showMessage: false,
  count: 0,
  bankAccounts: [],
  bankAccountList: [],
  bankAccountsByBranch: [],
};

export const fetchBankAccounts = createAsyncThunk(
  "fetchBankAccounts",
  async (payload, thunkAPI) => {
    try {
      const response = await BankAccountService.getBankAccounts(payload);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const searchBankAccounts = createAsyncThunk(
  "BankAccount/searchBankAccounts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await BankAccountService.searchBankAccounts(params);
      const payload = response?.data;

      if (payload && Array.isArray(payload.data)) {
        return {
          list: payload.data,
          count: payload.count ?? payload.data.length,
        };
      }
      if (Array.isArray(payload))
        return { list: payload, count: payload.length };
      if (Array.isArray(payload?.result))
        return { list: payload.result, count: payload.result.length };

      return { list: [], count: 0 };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchBankAccountsByBranch = createAsyncThunk(
  "BankAccount/fetchByBranch",
  async (branchId, thunkAPI) => {
    try {
      const response =
        await BankAccountService.getBankAccountsByBranch(branchId);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchBankAccountsByBranchAndPurpose = createAsyncThunk(
  "BankAccount/fetchByBranchAndPurpose",
  async ({ branchId, bankPurpose }, thunkAPI) => {
    try {
      const response =
        await BankAccountService.getBankAccountsByBranchAndPurpose(
          branchId,
          bankPurpose,
        );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

const BankAccountSlice = createSlice({
  name: "BankAccount",
  initialState,
  reducers: {
    resetStates: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
      state.bankAccounts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankAccounts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankAccounts = action.payload;
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      // fetch bank accounts by branch
      .addCase(fetchBankAccountsByBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBankAccountsByBranch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankAccountsByBranch = action.payload;
      })
      .addCase(fetchBankAccountsByBranch.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      // fetch bank accounts by branch and purpose
      .addCase(fetchBankAccountsByBranchAndPurpose.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchBankAccountsByBranchAndPurpose.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.bankAccountsByBranch = action.payload;
        },
      )
      .addCase(
        fetchBankAccountsByBranchAndPurpose.rejected,
        (state, action) => {
          state.isLoading = false;
          state.isError = true;
          state.message = action.payload;
        },
      )

      .addCase(searchBankAccounts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(searchBankAccounts.fulfilled, (state, action) => { 
        state.isLoading = false;
        state.bankAccountList = action.payload.list;
        state.count = action.payload.count;
      })
      .addCase(searchBankAccounts.rejected, (state) => {
        state.isLoading = false;
        notification.error({
          message: "Gagal memuat data Bank Account",
          description:
            "Tidak dapat terhubung ke server. Pastikan FT.LoanManagement.WebApi berjalan.",
          duration: 5,
        });
      });
  },
});

export const { showNotification, hideNotification, showLoading, resetStates } =
  BankAccountSlice.actions;

export default BankAccountSlice.reducer;
